import { useState, useRef, useEffect } from 'react';
import { GripVertical, Pencil, Copy, Trash2, ExternalLink } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ProjectoPLItem, ProjectoPLItemInsert, ProjectoItemPaymentStatus } from '../../types/projecto';
import { PROJECTO_ITEM_TYPE_CONFIG } from '../../types/projecto';
import { getItemEntryKind, getItemCommissionAmount, getPassthroughSpend } from '../../services/projectoService';

interface Props {
  item: ProjectoPLItem;
  isEditing: boolean;
  onInlineUpdate: (id: string, updates: Partial<ProjectoPLItemInsert>) => Promise<void>;
  onEditDetail: (item: ProjectoPLItem) => void;
  onDuplicate: (item: ProjectoPLItem) => void;
  onDelete: (item: ProjectoPLItem) => void;
}

const PAYMENT_STATUSES: ProjectoItemPaymentStatus[] = ['Pending', 'Invoiced', 'Paid', 'Cancelled'];

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700',
  Invoiced: 'bg-blue-100 text-blue-700',
  Paid: 'bg-emerald-100 text-emerald-700',
  Cancelled: 'bg-gray-100 text-gray-500',
};

function fmt(v: number) {
  return v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

export function PLSpreadsheetRow({
  item,
  isEditing,
  onInlineUpdate,
  onEditDetail,
  onDuplicate,
  onDelete,
}: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [editingField]);

  const startEdit = (field: string, value: string | number) => {
    if (!isEditing) return;
    setEditingField(field);
    const numericFields = ['unit_price', 'margin_pct', 'cost_commission_pct'];
    setTempValue(numericFields.includes(field) && value === 0 ? '' : String(value));
  };

  const cancelEdit = () => {
    setEditingField(null);
    setTempValue('');
  };

  const commitEdit = async (field: string) => {
    if (saving) return;
    const updates: Partial<ProjectoPLItemInsert> = {};
    let changed = false;

    if (field === 'name') {
      const v = tempValue.trim();
      if (v && v !== item.name) { updates.name = v; changed = true; }
    } else if (field === 'quantity') {
      const v = parseFloat(tempValue) || 1;
      if (v !== item.quantity) { updates.quantity = v; changed = true; }
    } else if (field === 'unit_price') {
      const v = parseFloat(tempValue) || 0;
      if (v !== item.unit_price) { updates.unit_price = v; changed = true; }
    } else if (field === 'margin_pct') {
      const v = Math.max(0, Math.min(100, parseFloat(tempValue) || 0));
      if (v !== item.margin_pct) { updates.margin_pct = v; changed = true; }
    } else if (field === 'cost_commission_pct') {
      const v = Math.max(0, Math.min(100, parseFloat(tempValue) || 0));
      if (v !== item.cost_commission_pct) { updates.cost_commission_pct = v; changed = true; }
    } else if (field === 'payment_status') {
      const v = tempValue as ProjectoItemPaymentStatus;
      if (v !== item.payment_status) { updates.payment_status = v; changed = true; }
    }

    setEditingField(null);
    setTempValue('');

    if (changed) {
      setSaving(true);
      try {
        await onInlineUpdate(item.id, updates);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit(field);
    } else if (e.key === 'Escape') {
      cancelEdit();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commitEdit(field);
      const fields = ['name', 'quantity', 'unit_price'];
      const idx = fields.indexOf(field);
      if (idx >= 0 && idx < fields.length - 1) {
        const nextField = fields[idx + 1];
        const val = getFieldValue(nextField);
        setTimeout(() => startEdit(nextField, val), 50);
      }
    }
  };

  const getFieldValue = (field: string): string | number => {
    switch (field) {
      case 'name': return item.name;
      case 'quantity': return item.quantity;
      case 'unit_price': return item.unit_price;
      case 'margin_pct': return item.margin_pct;
      case 'cost_commission_pct': return item.cost_commission_pct;
      case 'payment_status': return item.payment_status;
      default: return '';
    }
  };

  const entryKind = getItemEntryKind(item);
  const typeCfg = PROJECTO_ITEM_TYPE_CONFIG[item.item_type];
  const total = item.quantity * item.unit_price;
  const commissionAmt = getItemCommissionAmount(item);

  const cellBase = 'px-2 py-1.5 text-sm border-r border-gray-100 last:border-r-0';
  const editableCellBase = isEditing
    ? `${cellBase} cursor-text hover:bg-blue-50/50 transition-colors`
    : cellBase;

  const renderCell = (field: string, displayValue: React.ReactNode, rawValue: string | number, type: 'text' | 'number' | 'select' = 'text', extraCls = '') => {
    if (editingField === field) {
      if (type === 'select' && field === 'payment_status') {
        return (
          <td className={`${cellBase} ${extraCls} p-0`}>
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={tempValue}
              onChange={(e) => {
                setTempValue(e.target.value);
                setTimeout(() => commitEdit(field), 0);
              }}
              onBlur={() => commitEdit(field)}
              className="w-full h-full px-2 py-1.5 text-sm bg-blue-50 border-2 border-blue-400 rounded focus:outline-none"
            >
              {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </td>
        );
      }
      return (
        <td className={`${cellBase} ${extraCls} p-0`}>
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type === 'number' ? 'number' : 'text'}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={() => commitEdit(field)}
            onKeyDown={(e) => handleKeyDown(e, field)}
            min={type === 'number' ? 0 : undefined}
            max={field === 'margin_pct' || field === 'cost_commission_pct' ? 100 : undefined}
            step={type === 'number' ? (field === 'unit_price' ? 100 : 'any') : undefined}
            className="w-full h-full px-2 py-1.5 text-sm bg-blue-50 border-2 border-blue-400 rounded focus:outline-none tabular-nums"
          />
        </td>
      );
    }

    return (
      <td
        className={`${editableCellBase} ${extraCls} ${saving ? 'opacity-50' : ''}`}
        onDoubleClick={() => isEditing && startEdit(field, rawValue)}
      >
        {displayValue}
      </td>
    );
  };

  const renderCommissionCell = () => {
    if (entryKind === 'revenue') {
      return renderCell(
        'margin_pct',
        <div className="flex flex-col items-end">
          <span className="tabular-nums text-emerald-700 font-medium text-sm">
            {commissionAmt > 0 ? fmt(commissionAmt) : '\u2014'}
          </span>
          {item.margin_pct > 0 && (
            <span className="text-[10px] text-emerald-600/70 tabular-nums">
              {item.margin_pct % 1 === 0 ? item.margin_pct : item.margin_pct.toFixed(1)}%
            </span>
          )}
        </div>,
        item.margin_pct,
        'number',
        'w-24 text-right'
      );
    }

    if (entryKind === 'passthrough') {
      const spend = getPassthroughSpend(item);
      return (
        <td
          className={`${cellBase} w-24 text-right ${isEditing ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
          onDoubleClick={() => isEditing && onEditDetail(item)}
          title={`Cobrado ${fmt(total)} / Gasto ${fmt(spend)}`}
        >
          <div className="flex flex-col items-end">
            <span className="tabular-nums text-amber-700 font-medium text-sm">
              {commissionAmt > 0 ? fmt(commissionAmt) : '\u2014'}
            </span>
            {spend > 0 && (
              <span className="text-[10px] text-amber-600/70 tabular-nums">
                gasto {fmt(spend)}
              </span>
            )}
          </div>
        </td>
      );
    }

    const commissionMode = item.cost_commission_mode ?? 'percent';
    if (commissionMode === 'percent') {
      return renderCell(
        'cost_commission_pct',
        <div className="flex flex-col items-end">
          <span className="tabular-nums text-blue-700 font-medium text-sm">
            {commissionAmt > 0 ? fmt(commissionAmt) : '\u2014'}
          </span>
          {(item.cost_commission_pct ?? 0) > 0 && (
            <span className="text-[10px] text-blue-600/70 tabular-nums">
              {item.cost_commission_pct % 1 === 0 ? item.cost_commission_pct : item.cost_commission_pct.toFixed(1)}%
            </span>
          )}
        </div>,
        item.cost_commission_pct ?? 0,
        'number',
        'w-24 text-right'
      );
    }

    return (
      <td
        className={`${cellBase} w-24 text-right ${isEditing ? 'cursor-pointer hover:bg-blue-50/50' : ''}`}
        onDoubleClick={() => isEditing && onEditDetail(item)}
        title="Comissão fixa"
      >
        <div className="flex flex-col items-end">
          <span className="tabular-nums text-blue-700 font-medium text-sm">
            {commissionAmt > 0 ? fmt(commissionAmt) : '\u2014'}
          </span>
          <span className="text-[10px] text-blue-600/70">fixa</span>
        </div>
      </td>
    );
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`group border-b border-gray-50 ${isDragging ? 'opacity-50 bg-blue-50 shadow-lg z-10' : 'hover:bg-gray-50/50'} ${saving ? 'pointer-events-none' : ''}`}
    >
      {isEditing && (
        <td className="w-8 px-1 py-1.5 text-center">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-0.5 text-gray-300 hover:text-gray-500 touch-none"
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        </td>
      )}

      <td className={`${cellBase} w-16`}>
        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold leading-tight ${typeCfg.bg} ${typeCfg.color} truncate max-w-full`}>
          {item.item_type}
        </span>
      </td>

      {renderCell(
        'name',
        <span className="font-medium text-gray-900 truncate block max-w-[200px]" title={item.name}>
          {item.name}
          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-block ml-1 text-blue-400 hover:text-blue-600"
            >
              <ExternalLink className="w-3 h-3 inline" />
            </a>
          )}
        </span>,
        item.name,
        'text',
        'min-w-[140px]'
      )}

      {renderCell(
        'quantity',
        <span className="tabular-nums text-gray-700 text-right block">{item.quantity}</span>,
        item.quantity,
        'number',
        'w-14 text-right'
      )}

      {renderCell(
        'unit_price',
        <span className="tabular-nums text-gray-700 text-right block">{fmt(item.unit_price)}</span>,
        item.unit_price,
        'number',
        'w-24 text-right'
      )}

      <td className={`${cellBase} w-24 text-right`}>
        <span className="tabular-nums font-semibold text-gray-900">{fmt(total)}</span>
      </td>

      {renderCommissionCell()}

      {renderCell(
        'payment_status',
        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${PAYMENT_STATUS_COLORS[item.payment_status] ?? 'bg-gray-100 text-gray-600'}`}>
          {item.payment_status}
        </span>,
        item.payment_status,
        'select',
        'w-24'
      )}

      <td className={`${cellBase} w-20`}>
        <div className="flex items-center gap-0.5 justify-end">
          <button
            onClick={() => onEditDetail(item)}
            className="p-1 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
            title="Editar detalhes"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {isEditing && (
            <>
              <button
                onClick={() => onDuplicate(item)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                title="Duplicar"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onDelete(item)}
                className="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                title="Eliminar"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
